import { useState, useEffect, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Snackbar, Alert } from '@mui/material';
import { Routes, Route } from 'react-router-dom';
import { theme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useSocket } from './hooks/useSocket';
import { useConversations } from './hooks/useConversations';
import { useMessages } from './hooks/useMessages';
import { useSnackbar } from './hooks/useSnackbar';
import AppLayout from './components/AppLayout';

function AppContent() {
  const { isAuthenticated, token } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const snackbar = useSnackbar();

  const {
    conversations,
    selectedConversation,
    setSelectedConversation,
    loadConversations,
    loadConversationMessages,
    selectConversation,
    createNewConversation,
    renameConv,
    deleteConv,
    updateConversationTitleLocal,
  } = useConversations(isAuthenticated, token, snackbar.show);

  const {
    messages,
    isStreaming,
    clearMessages,
    loadMessages,
    sendMessage,
    handleMessageSync,
  } = useMessages(selectedConversation, loadConversationMessages);

  // 对话同步回调
  const handleConversationSync = useCallback((data) => {
    switch (data.type) {
      case 'conversation_created':
        snackbar.show(`对话已创建，刷新后生效`);
        break;
      case 'conversation_renamed':
        snackbar.show(`对话已重命名为 ${data.newTitle}，刷新后生效`);
        break;
      case 'conversation_deleted':
        snackbar.show(`对话已删除，刷新后生效`);
        break;
      default:
        break;
    }
  }, [snackbar.show]); // eslint-disable-line react-hooks/exhaustive-deps

  // AI自动生成标题回调
  const handleTitleUpdated = useCallback((data) => {
    if (data.conversationId && data.title) {
      updateConversationTitleLocal(data.conversationId, data.title);
      // 如果当前选中的就是这个对话，不需要额外操作，因为 updateConversationTitleLocal 已经处理了
    }
  }, [updateConversationTitleLocal]);

  // Socket 连接
  useSocket(isAuthenticated, token, handleMessageSync, handleConversationSync, handleTitleUpdated);

  // 认证后加载对话列表
  useEffect(() => {
    if (isAuthenticated && token) {
      loadConversations();
    } else {
      setSelectedConversation(null);
      clearMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  // 选中对话时加载历史消息
  useEffect(() => {
    if (selectedConversation?._id) {
      loadMessages(String(selectedConversation._id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  // 新建对话
  const handleNewChat = useCallback(async () => {
    if (!isAuthenticated) {
      setLoginModalOpen(true);
      return;
    }
    await createNewConversation();
  }, [isAuthenticated, createNewConversation]);

  // 发送消息
  const handleSendMessage = useCallback((content) => {
    if (!isAuthenticated) {
      snackbar.show('请先登录', 'warning');
      setTimeout(() => setLoginModalOpen(true), 1000);
      return;
    }
    if (!selectedConversation) {
      snackbar.show('请先选择一个对话', 'warning');
      return;
    }
    sendMessage(content);
  }, [isAuthenticated, selectedConversation, sendMessage, snackbar]);



  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
      }}>
        <Routes>
          <Route path="/" element={
            <AppLayout
              conversations={conversations}
              selectedConversation={selectedConversation}
              messages={messages}
              isStreaming={isStreaming}
              sidebarCollapsed={sidebarCollapsed}
              loginModalOpen={loginModalOpen}
              onConversationSelect={selectConversation}
              onNewChat={handleNewChat}
              onRenameConversation={renameConv}
              onDeleteConversation={deleteConv}
              onSendMessage={handleSendMessage}
              onSidebarToggle={() => setSidebarCollapsed(prev => !prev)}
              onCloseLoginModal={() => setLoginModalOpen(false)}
            />
          } />
          <Route path="/conversation/:conversationId" element={
            <AppLayout
              conversations={conversations}
              selectedConversation={selectedConversation}
              messages={messages}
              isStreaming={isStreaming}
              sidebarCollapsed={sidebarCollapsed}
              loginModalOpen={loginModalOpen}
              onConversationSelect={selectConversation}
              onNewChat={handleNewChat}
              onRenameConversation={renameConv}
              onDeleteConversation={deleteConv}
              onSendMessage={handleSendMessage}
              onSidebarToggle={() => setSidebarCollapsed(prev => !prev)}
              onCloseLoginModal={() => setLoginModalOpen(false)}
            />
          } />
        </Routes>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={snackbar.close}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={snackbar.close} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}